import { Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Sentry from '@sentry/angular';

import { ServiceBase } from '..';

import { DefaultLabel, FilterDefault, LocaleFormat } from 'src/app/core/models';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class DefaultsService extends ServiceBase {
  areLabelsLoading: WritableSignal<boolean> = signal(false);
  areFiltersLoading: WritableSignal<boolean> = signal(false);
  selectedClientId: WritableSignal<number | null> = signal(null);
  localeFormat: LocaleFormat;
  defaultLabels?: Array<DefaultLabel> = undefined;
  defaultLabelsLoaded = new Subject<Array<DefaultLabel>>();
  localeFormats: Array<LocaleFormat> = [];
  filterDefaults?: Array<FilterDefault> = undefined;
  filterDefaultsLoaded = new Subject<Array<FilterDefault>>();
  filterDefaultsLoading: WritableSignal<boolean> = signal(false);
  filterDefaultsClient: WritableSignal<number | null> = signal(null);
  constructor(
    public http: HttpClient,
    public translateService: TranslateService
  ) {
    super(translateService);
  }

  getDefaultLabels = (clientId: number) => {
    this.areLabelsLoading.set(true);
    const url = `${this.baseUrl}mobile-metrics/default-labels`;
    this.http
      .post<Array<DefaultLabel>>(url, this.getPostBody(clientId))
      .subscribe((labels) => {
        this.areLabelsLoading.set(false);
        this.defaultLabels = [...labels];
        this.defaultLabelsLoaded.next(this.defaultLabels);
      });
  };

  getFilterDefaults = (clientId: number, roleId?: number) => {
    console.log('defaults.service.getFilterDefaults  ', clientId);
    this.filterDefaultsClient.set(clientId);
    this.filterDefaultsLoading.set(true);
    // This limits making another call to the api for the same clientId which
    // was happening every time the app loaded.
    if (this.selectedClientId() === clientId) {
      return;
    }
    this.areFiltersLoading.set(true);
    this.selectedClientId.set(clientId);
    const url = `${this.baseUrl}mobile-metrics/filter-defaults`;
    this.http
      .post<
        Array<FilterDefault>
      >(url, this.getPostBody(clientId), { headers: this.getApiVersionHeader() })
      .subscribe(
        (defaults) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const clientCurrencySymbolFilterDefault = this.getFilterDefault(
            'client.currency-symbol',
            defaults
          )!;

          if (this.localeFormats && this.localeFormats.length) {
            this.setFormatLocale(clientCurrencySymbolFilterDefault);
          } else {
            const url = `${this.baseUrl}mobile-metrics/locale-formats`;
            this.http.get<Array<LocaleFormat>>(url).subscribe((formats) => {
              this.localeFormats = [...formats];
              this.setFormatLocale(clientCurrencySymbolFilterDefault);
            });
          }
          this.filterDefaultsLoading.set(false);
          this.filterDefaults = defaults;
          this.filterDefaultsLoaded.next(defaults);
        },
        (error) => {
          if (error.status === '401') {
            Sentry.captureException(error);
          }
        }
      );
  };

  getFilterDefault = (
    codeName: string,
    filterDefaults?: Array<FilterDefault>
  ): FilterDefault | undefined => {
    const defaults = filterDefaults || this.filterDefaults;
    const item = defaults?.find((x) => x.CodeName === codeName);
    return item;
  };

  setFormatLocale = (clientCurrencySymbolFilterDefault: FilterDefault) => {
    if (
      clientCurrencySymbolFilterDefault &&
      clientCurrencySymbolFilterDefault.DefaultValue
    ) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.localeFormat = this.localeFormats.find(
        (x) =>
          x.LocaleFormatID === clientCurrencySymbolFilterDefault.DefaultValue
      )!;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.localeFormat = null!;
    }
  };
}
